import React from "react";
import { parseErrorMessage } from "@/lib/parseErrorMessage";
import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useAdAccount } from "@/contexts/AdAccountContext";
import { useCachedFacebookPages } from "@/hooks/useCachedFacebookPages";
import { useCachedInstagramAccounts } from "@/hooks/useCachedInstagramAccounts";
import { useCachedPartnershipFacebookPages, useCachedPartnershipInstagram } from "@/hooks/useCachedPartnershipAccounts";
import { supabase } from "@/integrations/supabase/client";
import { AdTemplate, AdTemplateFormData } from "@/types/template";
import { CreativeEnhancementsSection } from "./CreativeEnhancementsSection";
import {
  templateToToggleValues,
  toggleValuesToTemplateColumns,
} from "@/utils/enhancementMappingUtils";
import { useEnhancementConfig } from "@/hooks/useEnhancementConfig";
import { SitelinksSection } from "@/components/shared/SitelinksSection";
import type { SitelinkItem } from "@/components/shared/SitelinksSection";

interface TemplateFormProps {
  template?: AdTemplate;
  onSuccess: () => void;
}

const CTA_BUTTONS = [
  "NO_BUTTON",
  "APPLY_NOW", "BOOK_NOW", "CONTACT_US", "DONATE_NOW", "DOWNLOAD",
  "GET_OFFER", "GET_QUOTE", "GET_SHOWTIMES", "LEARN_MORE", "LISTEN_NOW",
  "ORDER_NOW", "PLAY_GAME", "REQUEST_TIME", "SEE_MENU", "SHOP_NOW",
  "SIGN_UP", "SUBSCRIBE", "WATCH_MORE",
];

export default function TemplateForm({ template, onSuccess }: TemplateFormProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { selectedAdAccountId } = useAdAccount();
  const { data: enhancementConfig } = useEnhancementConfig();
  const toggles = enhancementConfig?.toggles ?? [];

  const { data: facebookPages = [], isLoading: loadingPages } = useCachedFacebookPages(user?.id, {
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d || d.length === 0) return 3000;
      return false;
    },
  });
  const { data: instagramAccounts = [], isLoading: loadingInstagram } = useCachedInstagramAccounts(user?.id, {
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d || d.length === 0) return 3000;
      return false;
    },
  });

  // Form state
  const [formData, setFormData] = useState<AdTemplateFormData>({
    name: template?.name || "",
    primary_text_1: template?.primary_text_1 || "",
    primary_text_2: template?.primary_text_2 || "",
    primary_text_3: template?.primary_text_3 || "",
    primary_text_4: template?.primary_text_4 || "",
    primary_text_5: template?.primary_text_5 || "",
    headline_1: template?.headline_1 || "",
    headline_2: template?.headline_2 || "",
    headline_3: template?.headline_3 || "",
    headline_4: template?.headline_4 || "",
    headline_5: template?.headline_5 || "",
    cta_button: template?.cta_button || "",
    url_destination: template?.url_destination || "",
    url_parameters: template?.url_parameters || "",
    facebook_page_name: template?.facebook_page_name || "",
    facebook_page_id: template?.facebook_page_id || "",
    instagram_name: template?.instagram_name || "",
    instagram_id: template?.instagram_id || "",
    partner_facebook_page_name: template?.partner_facebook_page_name || "",
    partner_facebook_page_id: template?.partner_facebook_page_id || "",
    partner_instagram_name: template?.partner_instagram_name || "",
    partner_instagram_id: template?.partner_instagram_id || "",
    pixel_name: template?.pixel_name || "",
    pixel_id: template?.pixel_id || "",
    status: template?.status || "PAUSED",
    ad_account_id: template?.ad_account_id || selectedAdAccountId || "",
  });

  // Partnership accounts
  const { data: partnerFbPages = [], isLoading: loadingPartnerFb } = useCachedPartnershipFacebookPages(user?.id, formData.ad_account_id || undefined);
  const { data: partnerIgAccounts = [], isLoading: loadingPartnerIg } = useCachedPartnershipInstagram(user?.id, formData.ad_account_id || undefined);

  // Sitelinks
  const [sitelinks, setSitelinks] = useState<SitelinkItem[]>(() => {
    if (template?.sitelinks && Array.isArray(template.sitelinks)) {
      return template.sitelinks as SitelinkItem[];
    }
    return [];
  });

  // -----------------------------------------------------------------------
  // Enhancement state: single flat Record<toggleKey, boolean>
  // Mapped from template columns via shared mapping utility
  // -----------------------------------------------------------------------
  const [enhancementValues, setEnhancementValues] = useState<Record<string, boolean>>(() =>
    template ? templateToToggleValues(template as unknown as Record<string, unknown>, toggles) : {}
  );

  const handleEnhancementChange = (key: string, value: boolean) => {
    setEnhancementValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleEnhancementBatchChange = (updates: Record<string, boolean>) => {
    setEnhancementValues((prev) => ({ ...prev, ...updates }));
  };

  const [enhancementsSectionOpen, setEnhancementsSectionOpen] = useState(false);

  // Check if any site_links toggle is on (for showing sitelinks section)
  const showSitelinksSection = useMemo(() => {
    return Object.entries(enhancementValues).some(
      ([key, val]) => (key.includes("site_links") || key.includes("site_extensions")) && val
    );
  }, [enhancementValues]);

  // Loading states
  const [saving, setSaving] = useState(false);
  const loadingAssets = loadingPages || loadingInstagram || loadingPartnerFb || loadingPartnerIg;

  const handleFacebookPageChange = (displayName: string) => {
    if (!displayName || displayName === "__NONE__") {
      setFormData((prev) => ({ ...prev, facebook_page_name: "", facebook_page_id: "" }));
      return;
    }
    const match = displayName.match(/\(([^)]+)\)$/);
    const pageId = match ? match[1] : "";
    const pageName = displayName.replace(/\s*\([^)]+\)$/, "");
    setFormData((prev) => ({ ...prev, facebook_page_name: pageName, facebook_page_id: pageId }));
  };

  const handleInstagramChange = (displayName: string) => {
    if (!displayName || displayName === "__NONE__") {
      setFormData((prev) => ({ ...prev, instagram_name: "", instagram_id: "" }));
      return;
    }
    const match = displayName.match(/\(([^)]+)\)$/);
    const instagramId = match ? match[1] : "";
    const instagramName = displayName.replace(/\s*\([^)]+\)$/, "").replace("@", "");
    setFormData((prev) => ({ ...prev, instagram_name: instagramName, instagram_id: instagramId }));
  };

  const handlePartnerFacebookPageChange = (displayName: string) => {
    if (!displayName || displayName === "__NONE__") {
      setFormData((prev) => ({ ...prev, partner_facebook_page_name: "", partner_facebook_page_id: "" }));
      return;
    }
    const match = displayName.match(/\(([^)]+)\)$/);
    const pageId = match ? match[1] : "";
    const pageName = displayName.replace(/\s*\([^)]+\)$/, "");
    setFormData((prev) => ({ ...prev, partner_facebook_page_name: pageName, partner_facebook_page_id: pageId }));
  };

  const handlePartnerInstagramChange = (displayName: string) => {
    if (!displayName || displayName === "__NONE__") {
      setFormData((prev) => ({ ...prev, partner_instagram_name: "", partner_instagram_id: "" }));
      return;
    }
    const match = displayName.match(/\(([^)]+)\)$/);
    const instagramId = match ? match[1] : "";
    const instagramName = displayName.replace(/\s*\([^)]+\)$/, "").replace("@", "");
    setFormData((prev) => ({ ...prev, partner_instagram_name: instagramName, partner_instagram_id: instagramId }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (!user) {
        toast({ title: "Error", description: "You must be logged in", variant: "destructive" });
        return;
      }

      // Convert toggle values → template column data
      const enhancementColumns = toggleValuesToTemplateColumns(enhancementValues, toggles);

      const templateData = {
        ...formData,
        ...enhancementColumns,
        sitelinks: sitelinks as unknown,
        user_id: user.id,
        ad_account_id:
          formData.ad_account_id && formData.ad_account_id.trim() !== ""
            ? formData.ad_account_id
            : null,
      };

      if (template) {
        const { error } = await supabase.from("ad_templates").update(templateData as any).eq("id", template.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("ad_templates").insert([templateData as any]);
        if (error) throw error;
      }

      toast({
        title: "Success",
        description: template ? "Template updated successfully" : "Template created successfully",
      });
      onSuccess();
    } catch (error: unknown) {
      console.error("Failed to save template:", error);
      toast({ title: "Error", description: parseErrorMessage(error), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{template ? "Edit Template" : "Create Template"}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Template Name */}
          <div>
            <Label htmlFor="name">Template Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Summer Sale Template"
              required
            />
          </div>

          {/* Primary Texts */}
          <div className="space-y-2">
            <Label>Primary Texts</Label>
            {[1, 2, 3, 4, 5].map((num) => (
              <Textarea
                key={num}
                placeholder={`Primary text ${num}`}
                value={formData[`primary_text_${num}` as keyof AdTemplateFormData] as string}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, [`primary_text_${num}`]: e.target.value }))
                }
                rows={2}
              />
            ))}
          </div>

          {/* Headlines */}
          <div className="space-y-2">
            <Label>Headlines</Label>
            {[1, 2, 3, 4, 5].map((num) => (
              <Input
                key={num}
                placeholder={`Headline ${num}`}
                value={formData[`headline_${num}` as keyof AdTemplateFormData] as string}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, [`headline_${num}`]: e.target.value }))
                }
              />
            ))}
          </div>

          {/* CTA Button */}
          <div>
            <Label htmlFor="cta_button">CTA Button</Label>
            <Select
              value={formData.cta_button || ""}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, cta_button: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select CTA button" />
              </SelectTrigger>
              <SelectContent>
                {CTA_BUTTONS.map((cta) => (
                  <SelectItem key={cta} value={cta}>{cta.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* URL Destination */}
          <div>
            <Label htmlFor="url_destination">URL Destination</Label>
            <Input
              id="url_destination"
              type="url"
              value={formData.url_destination}
              onChange={(e) => setFormData((prev) => ({ ...prev, url_destination: e.target.value }))}
              placeholder="https://example.com"
            />
          </div>

          {/* URL Parameters */}
          <div>
            <Label htmlFor="url_parameters">URL Parameters</Label>
            <Input
              id="url_parameters"
              value={formData.url_parameters}
              onChange={(e) => setFormData((prev) => ({ ...prev, url_parameters: e.target.value }))}
              placeholder="utm_source=facebook&utm_medium=cpc"
            />
          </div>

          {/* Facebook Page */}
          <div>
            <Label htmlFor="facebook_page">Facebook Page</Label>
            <Select
              value={formData.facebook_page_id ? `${formData.facebook_page_name} (${formData.facebook_page_id})` : "__NONE__"}
              onValueChange={handleFacebookPageChange}
              disabled={loadingAssets}
            >
              <SelectTrigger>
                <SelectValue placeholder={loadingAssets ? "Loading..." : "Select Facebook page"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__NONE__">None</SelectItem>
                {facebookPages.map((page) => (
                  <SelectItem key={page.page_id} value={`${page.page_name} (${page.page_id})`}>
                    {page.page_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Instagram Account */}
          <div>
            <Label htmlFor="instagram">Instagram Account</Label>
            <Select
              value={formData.instagram_id ? `@${formData.instagram_name} (${formData.instagram_id})` : "__NONE__"}
              onValueChange={handleInstagramChange}
              disabled={loadingAssets}
            >
              <SelectTrigger>
                <SelectValue placeholder={loadingAssets ? "Loading..." : "Select Instagram account"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__NONE__">None</SelectItem>
                {instagramAccounts.map((account) => (
                  <SelectItem key={account.instagram_id} value={`@${account.username} (${account.instagram_id})`}>
                    @{account.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Partner Facebook Page */}
          <div>
            <Label htmlFor="partner_facebook">Partner Facebook Page (Optional)</Label>
            <Select
              value={formData.partner_facebook_page_id ? `${formData.partner_facebook_page_name} (${formData.partner_facebook_page_id})` : "__NONE__"}
              onValueChange={handlePartnerFacebookPageChange}
              disabled={loadingAssets}
            >
              <SelectTrigger>
                <SelectValue placeholder={loadingAssets ? "Loading..." : "Select Partner Facebook page"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__NONE__">None</SelectItem>
                {partnerFbPages.map((page) => (
                  <SelectItem key={`partner-${page.page_id}`} value={`${page.page_name} (${page.page_id})`}>
                    {page.page_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Partner Instagram */}
          <div>
            <Label htmlFor="partner_instagram">Partner Instagram (Optional)</Label>
            <Select
              value={formData.partner_instagram_id ? `@${formData.partner_instagram_name} (${formData.partner_instagram_id})` : "__NONE__"}
              onValueChange={handlePartnerInstagramChange}
              disabled={loadingAssets}
            >
              <SelectTrigger>
                <SelectValue placeholder={loadingAssets ? "Loading..." : "Select Partner Instagram"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__NONE__">None</SelectItem>
                {partnerIgAccounts.map((account) => (
                  <SelectItem key={`partner-${account.instagram_id}`} value={`@${account.username} (${account.instagram_id})`}>
                    @{account.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status */}
          <div>
            <Label htmlFor="status">Default Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="PAUSED">Paused</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Creative Enhancements Section — uses shared EnhancementTogglesPanel */}
      <CreativeEnhancementsSection
        values={enhancementValues}
        onChange={handleEnhancementChange}
        onBatchChange={handleEnhancementBatchChange}
        isOpen={enhancementsSectionOpen}
        onOpenChange={setEnhancementsSectionOpen}
      />

      {/* Sitelinks Section */}
      {showSitelinksSection && (
        <SitelinksSection sitelinks={sitelinks} onChange={setSitelinks} />
      )}

      {/* Action Buttons */}
      <div className="flex gap-4">
        <Button type="submit" disabled={saving}>
          {saving ? "Saving..." : template ? "Update Template" : "Create Template"}
        </Button>
        <Button type="button" variant="outline" onClick={() => navigate("/launcher/templates")}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
